import {
	defaultTileGridConfiguration,
	defaultTileGroupGridDimensionLimits,
	DimensionLimitations,
	PLACEHOLDER_ID,
	PlaceHolderId as PlaceholderId,
	SpaceOptions,
	SpaceRenderState,
	TileGridConfiguration,
	TileGridRenderState,
	TileGroupPlaceHolderRenderState,
	TileGroupRenderState,
	TileRenderState,
	WorkspaceInitializationModel
} from "../common";
import { GridItem, GridState } from "client_legacy/Workspace/gridState";
import {
	DatabaseOrTempObjectId,
	DatabaseOrTempObjectIdMap,
	TempId
} from "client_legacy/ndaw/tempOrDatabaseId";
import {
	clamp,
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	hasFlag
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { Guid } from "client_legacy/ndaw/guid";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { TempIdToDatabaseIdMappingModel } from "client/Api/common";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import {
	SpaceKind,
	TileGroupAccessKind,
	TileGroupSpacePreferenceData,
	TileGroupType,
	TileGroupWidth
} from "client/Api/Workspace/workspace";
import {
	SpaceReducerContext,
	SpaceChangeEffect,
	TileGroupReducerContext
} from "./effects";
import { ModifiedCollectionBuilder, setPropertiesPreservingRefWhenUnchanged } from "./common";
import { GridDimension, GridPosition } from "client/Api/Workspace/common";
import { TileDropTarget } from "../selectors/space";
import * as spaceSelectors from "../selectors/space";
import * as tileGroupSelectors from "../selectors/tileGroup";
import * as tileGroupReducers from "./tileGroup";
import * as tileReducers from "./tile";

export function generateInitialState(
	initializationModel: WorkspaceInitializationModel,
	automaticallyReorderTilesForNoneEditableTileGroups: boolean,
	regularGroupWidthTileGridConfiguration: TileGridConfiguration = defaultTileGridConfiguration,
	tileGroupGridDimensionLimits: DimensionLimitations = defaultTileGroupGridDimensionLimits
): SpaceRenderState {
	const context = new SpaceReducerContext();
	const { groups } = initializationModel;

	const isInEditMode: boolean = false;

	const groupRenderStates: TileGroupRenderState[] = groups.map(
		(group, groupOrder) => tileGroupReducers.generateInitialState(
			context.createGroupContext(),
			group,
			groupOrder,
			automaticallyReorderTilesForNoneEditableTileGroups,
			regularGroupWidthTileGridConfiguration,
			tileGroupGridDimensionLimits,
			isInEditMode
		)
	);

	const result: Omit<SpaceRenderState, "dimension"> = {
		groups: groupRenderStates,
		placeholder: undefined,
		options: { isSet: false },
		tileGroupGridDimensionLimits,
		regularGroupWidthTileGridConfiguration,
		isInEditMode,
		automaticallyReorderTilesForNoneEditableTileGroups
	};

	return recalculateTileGroupPositions(result);
}

export function applyConfiguration(
	currentState: SpaceRenderState,
	tileGroupGridDimensionLimits: DimensionLimitations,
	regularGroupWidthTileGridConfiguration: TileGridConfiguration
): SpaceRenderState {
	const context = new SpaceReducerContext();

	const didTileGroupGridDimensionLimitsChange: boolean = !deepComparison(
		currentState.tileGroupGridDimensionLimits,
		tileGroupGridDimensionLimits
	);

	const didRegularGroupWidthTileGridConfigurationChange: boolean = !deepComparison(
		currentState.regularGroupWidthTileGridConfiguration,
		regularGroupWidthTileGridConfiguration
	);

	if (didTileGroupGridDimensionLimitsChange) {
		context.addEffect(SpaceChangeEffect.TileGroupGridRepackingIsRequired);
		currentState = {
			...currentState,
			tileGroupGridDimensionLimits
		};
	}

	if (didRegularGroupWidthTileGridConfigurationChange) {
		currentState = {
			...currentState,
			regularGroupWidthTileGridConfiguration
		};
	}

	currentState = updateTileGroups(
		currentState,
		context,
		(group, groupContext) => tileGroupReducers.applyDimensionLimits(
			group,
			groupContext,
			tileGroupGridDimensionLimits,
			regularGroupWidthTileGridConfiguration,
			currentState
				.automaticallyReorderTilesForNoneEditableTileGroups
		)
	);

	return applyEffect(currentState, context);
}

export function applyEditMode(
	currentState: SpaceRenderState,
	isInEditMode: boolean
): SpaceRenderState {
	const context = new SpaceReducerContext();

	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{ isInEditMode }
	);

	currentState = updateTileGroups(
		currentState,
		context,
		(group, groupContext) => tileGroupReducers.applyEditMode(
			group,
			groupContext,
			isInEditMode,
			currentState.options
		)
	);

	return applyEffect(currentState, context);
}

export function applySpace(
	currentState: SpaceRenderState,
	spaceOptions: OptionalModel<SpaceOptions>,
	tileGroupPreferences: readonly TileGroupSpacePreferenceData[]
): SpaceRenderState {
	const preferencesMap: ReadonlyMap<DatabaseOrTempObjectId, TileGroupInSpacePreference> = getPreferencesByTileGroupId(
		tileGroupPreferences
	);

	const orderMap: ReadonlyMap<DatabaseOrTempObjectId, number> = getOrderByTileGroupId(
		currentState.groups,
		preferencesMap
	);

	const context = new SpaceReducerContext();

	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{ options: spaceOptions }
	);

	currentState = updateTileGroups(
		currentState,
		context,
		(group: TileGroupRenderState, groupContext: TileGroupReducerContext): TileGroupRenderState => {
			const preference: TileGroupInSpacePreference | undefined = preferencesMap.get(group.data.id);

			const order: number = ensureNotNil(
				orderMap.get(group.data.id)
			);

			return tileGroupReducers.applySpacePreference(
				group,
				groupContext,
				!preference
					? { isSet: false }
					: {
						isSet: true,
						value: preference.isHidden
							? "hidden"
							: "visible"
					},
				order,
				spaceOptions,
				currentState.isInEditMode
			);
		}
	);

	context.addEffect(SpaceChangeEffect.TileGroupGridRepackingIsRequired);

	return applyEffect(currentState, context);
}

export function updateIdsOfNewlyCreatedElements(
	currentState: SpaceRenderState,
	addedItemsIdsMap: TempIdToDatabaseIdMappingModel[]
): SpaceRenderState {
	const actualMap: Map<Guid, number> = fromArray(
		addedItemsIdsMap,
		x => x.tempId,
		x => x.databaseId
	);

	return updateTileGroupsWithIdsOfNewlyCreatedElements(currentState, actualMap);
}

export function addTileGroup(
	currentState: SpaceRenderState,
	id: TempId,
	kind: TileGroupType,
	name: string
): SpaceRenderState {
	if (!currentState.options.isSet) {
		console.error("Cannot add tile group when there are no spaces.");
		return currentState;
	}

	const optionsValue: SpaceOptions = currentState.options
		.value;

	const isHiddenByDefaultOnPersonalSpace: boolean = optionsValue.kind !== SpaceKind.Personal;

	const context = new SpaceReducerContext();

	currentState = updateTileGroups(
		currentState,
		context,
		(group) => {
			return tileGroupReducers.applyOrder(
				group,
				group.order + 1
			);
		}
	);

	const groupContext: TileGroupReducerContext = context.createGroupContext();

	let tileGroup: TileGroupRenderState = tileGroupReducers.generateInitialState(
		groupContext,
		{
			data: {
				id,
				accessKind: TileGroupAccessKind.Edit,
				isHiddenByDefaultOnPersonalSpace,
				kind,
				name,
				width: TileGroupWidth.Regular
			},
			tiles: EMPTY_ARRAY
		},
		0,
		currentState.automaticallyReorderTilesForNoneEditableTileGroups,
		currentState.regularGroupWidthTileGridConfiguration,
		currentState.tileGroupGridDimensionLimits,
		currentState.isInEditMode
	);

	tileGroup = tileGroupReducers.applyVisibilityPreference(
		tileGroup,
		groupContext,
		{
			isSet: true,
			value: "visible"
		},
		currentState.isInEditMode,
		currentState.options
	);

	currentState = {
		...currentState,
		groups: [
			...currentState.groups,
			tileGroup
		]
	};

	context.addEffect(SpaceChangeEffect.TileGroupGridRepackingIsRequired);

	return applyEffect(currentState, context);
}

export function applyTileGroupVisibility(
	renderState: SpaceRenderState,
	tileGroupId: DatabaseOrTempObjectId,
	isVisible: boolean
): SpaceRenderState {
	return updateTileGroupAndApplyEffect(
		renderState,
		tileGroupId,
		(group, groupContext) => tileGroupReducers.applyVisibilityPreference(
			group,
			groupContext,
			{
				isSet: true,
				value: isVisible
					? "visible"
					: "hidden"
			},
			renderState.isInEditMode,
			renderState.options
		)
	);
}

export function changeTileGroupName(
	renderState: SpaceRenderState,
	tileGroupId: DatabaseOrTempObjectId,
	newName: string
): SpaceRenderState {
	return updateTileGroupAndApplyEffect(
		renderState,
		tileGroupId,
		group => tileGroupReducers.changeName(group, newName)
	);
}

export function resizeTileGroup(
	renderState: SpaceRenderState,
	tileGroupId: DatabaseOrTempObjectId,
	newWidth: TileGroupWidth
): SpaceRenderState {
	return updateTileGroupAndApplyEffect(
		renderState,
		tileGroupId,
		(group, groupContext) => tileGroupReducers.changeWidth(
			group,
			groupContext,
			newWidth,
			renderState.tileGroupGridDimensionLimits,
			renderState.regularGroupWidthTileGridConfiguration,
			renderState.automaticallyReorderTilesForNoneEditableTileGroups
		)
	);
}

export function deleteTileGroup(
	renderState: SpaceRenderState,
	tileGroupId: DatabaseOrTempObjectId
): SpaceRenderState {
	const context = new SpaceReducerContext();

	renderState = updateTileGroup(
		renderState,
		context,
		tileGroupId,
		() => "delete"
	);

	context.addEffect(SpaceChangeEffect.TileGroupGridRepackingIsRequired);

	return applyEffect(renderState, context);
}

export function applyTileGroupDragging(
	currentState: SpaceRenderState,
	tileGroupId: DatabaseOrTempObjectId,
	unboundPosition: GridPosition
): SpaceRenderState {
	return applyTileGroupDraggingInternal(
		currentState,
		tileGroupId,
		unboundPosition,
		false
	);
}

export function commitTileGroupDrag(
	currentState: SpaceRenderState,
	tileGroupId: DatabaseOrTempObjectId,
	unboundPosition: GridPosition
): SpaceRenderState {
	return applyTileGroupDraggingInternal(
		currentState,
		tileGroupId,
		unboundPosition,
		true
	);
}

export function applyTileDragging(
	currentState: SpaceRenderState,
	tileId: number,
	unboundGlobalPosition: GridPosition
): SpaceRenderState {
	return applyTileDraggingInternal(
		currentState,
		tileId,
		unboundGlobalPosition,
		false
	);
}

export function commitTileDrag(
	currentState: SpaceRenderState,
	tileId: number,
	unboundGlobalPosition: GridPosition
): SpaceRenderState {
	return applyTileDraggingInternal(
		currentState,
		tileId,
		unboundGlobalPosition,
		true
	);
}

export function deleteTile(
	currentState: SpaceRenderState,
	tileId: number
): SpaceRenderState {
	const tileGroupId: DatabaseOrTempObjectId = findTileGroupIdContainingTile(currentState, tileId);

	return updateTileGroupAndApplyEffect(
		currentState,
		tileGroupId,
		(state, context) => tileGroupReducers.deleteTile(
			state,
			context,
			tileId
		)
	);
}

export function resizeTile(
	currentState: SpaceRenderState,
	tileId: number,
	dimension: GridDimension
): SpaceRenderState {
	const tileGroupId: DatabaseOrTempObjectId = findTileGroupIdContainingTile(currentState, tileId);

	return updateTileGroupAndApplyEffect(
		currentState,
		tileGroupId,
		(group, groupContext) => {
			const maxColumns: number = group.tileGrid
				.settings
				.configuration
				.dimensionLimits
				.columns;

			if (dimension.columnSpan > maxColumns) {
				const newWidthResult: TryResult<TileGroupWidth> = tileGroupSelectors.selectNewWidth(
					group,
					currentState.regularGroupWidthTileGridConfiguration,
					dimension.columnSpan
				);

				if (!newWidthResult.wasSuccessful) {
					throw new Error(`Column span ${dimension.columnSpan} cannot fit into area of column span ${maxColumns}`);
				}

				group = tileGroupReducers.changeWidth(
					group,
					groupContext,
					newWidthResult.result,
					currentState.tileGroupGridDimensionLimits,
					currentState.regularGroupWidthTileGridConfiguration,
					currentState.automaticallyReorderTilesForNoneEditableTileGroups
				);
			}

			return tileGroupReducers.resizeTile(
				group,
				groupContext,
				tileId,
				dimension
			);
		}
	);
}

export function persistTileStateOfEditableGroups(
	currentState: SpaceRenderState
): SpaceRenderState {
	return updateTileGroupsAndApplyEffect(
		currentState,
		g => g.data.accessKind === TileGroupAccessKind.Edit,
		g => tileGroupReducers.persistGridState(g)
	);
}

function updateTileGroupsWithIdsOfNewlyCreatedElements(
	currentState: SpaceRenderState,
	addedItemsIdsMap: ReadonlyMap<Guid, number>
): SpaceRenderState {
	return updateTileGroupsAndApplyEffect(
		currentState,
		(group) => !group.data
			.id
			.hasDatabaseId,
		(group) => {
			return tileGroupReducers.applyIdOfNewlyCreatedTileGroup(
				group,
				addedItemsIdsMap
			);
		}
	);
}

function applyTileGroupDraggingInternal(
	currentState: SpaceRenderState,
	tileGroupId: DatabaseOrTempObjectId,
	unboundPosition: GridPosition,
	shouldCommit: boolean
): SpaceRenderState {
	const tileGroupBeingDragged: TileGroupRenderState | undefined = currentState.groups
		.find((x) => x.data.id.equals(tileGroupId));

	if (!tileGroupBeingDragged) {
		throw new Error("The tile group being dragged is not in the grid");
	}

	const state: GridState<TileGroupRenderState> = spaceSelectors
		.generateStateOfTileGroupsConsumingSpace(currentState);

	const { tileGroupGridDimensionLimits } = currentState;

	const stateDimension: GridDimension = state.dimension;
	stateDimension.columnSpan = Math.max(tileGroupGridDimensionLimits.minColumns, stateDimension.columnSpan);
	// Make it so the drag position is always inside the grid
	const boundPosition: GridPosition = {
		column: clamp(unboundPosition.column, 0, stateDimension.columnSpan - 1),
		row: clamp(unboundPosition.row, 0, stateDimension.rowSpan - 1)
	};

	const collisions: ReadonlyArray<GridItem<TileGroupRenderState>> = state.listCollisions({
		position: boundPosition,
		dimension:  tileGroupBeingDragged.dimension
	});

	const orderedGroupRenderStates: TileGroupRenderState[] = sortBy(
		currentState.groups,
		(x) => x.order
	);

	const indexOfItemBeingDragged: number = orderedGroupRenderStates.findIndex(
		(x) => x.data.id.equals(tileGroupId)
	);

	if (indexOfItemBeingDragged === -1) {
		throw new Error("The tile group being dragged is not in the grid");
	}

	let indexToInsert: number = orderedGroupRenderStates.length;
	if (collisions.length > 0) {
		const collisionItemId: DatabaseOrTempObjectId = collisions[0]
			.item
			.data
			.id;

		indexToInsert = orderedGroupRenderStates.findIndex(
			(x) => x.data.id.equals(collisionItemId)
		);
	}

	if (indexToInsert === -1) {
		throw new Error("TileGroup place to insert the tile group is not in the grid");
	}

	orderedGroupRenderStates.splice(
		indexToInsert,
		0,
		orderedGroupRenderStates.splice(
			indexOfItemBeingDragged,
			1
		)[0]
	);

	if (shouldCommit) {
		const orderMap: ReadonlyMap<DatabaseOrTempObjectId, number> = fromArray(
			() => new DatabaseOrTempObjectIdMap<number>(),
			orderedGroupRenderStates,
			(x) => x.data.id,
			(_, index) => index
		);

		const context = new SpaceReducerContext();
		const newGroups: TileGroupRenderState[] = [];

		for (const groupRenderState of currentState.groups) {
			const newOrder: number = ensureNotNil(
				orderMap.get(
					groupRenderState.data
						.id
				)
			);

			newGroups.push(
				tileGroupReducers.applyOrder(
					groupRenderState,
					newOrder
				)
			);
		}

		context.addEffect(SpaceChangeEffect.TileGroupGridRepackingIsRequired);

		const result: SpaceRenderState = {
			...currentState,
			groups: newGroups,
			placeholder: undefined
		};

		return applyEffect(result, context);
	} else {
		const placeholder: TileGroupPlaceHolderRenderState = {
			data: { id: PLACEHOLDER_ID },
			tileGroupId: tileGroupBeingDragged.data
				.id,
			dimension: tileGroupBeingDragged.dimension,
			// position is a dummy, wil be populated by applyGridState
			position: { column: 0, row: 0 }
		};

		const gridItems: GridItem<TileGroupRenderState | TileGroupPlaceHolderRenderState>[] = [];
		let currentColumn: number = 0;
		for (const groupRenderState of orderedGroupRenderStates) {
			if (!groupRenderState.isVisible) {
				continue;
			}

			if (groupRenderState === tileGroupBeingDragged) {
				const placeholderGridItem: GridItem<TileGroupPlaceHolderRenderState> = {
					position: {
						column: currentColumn++,
						row: 0
					},
					dimension: tileGroupBeingDragged.dimension,
					item: placeholder
				};

				gridItems.push(placeholderGridItem);
			} else {
				const gridItem: GridItem<TileGroupRenderState> = {
					position: {
						column: currentColumn++,
						row: 0
					},
					dimension: groupRenderState.dimension,
					item: groupRenderState
				};

				gridItems.push(gridItem);
			}
		}

		const newGridState = new GridState<TileGroupRenderState | TileGroupPlaceHolderRenderState>(gridItems)
			.apply(
				spaceSelectors.getTileGroupGridPackingAlgorithm(currentState)
			);

		let result: SpaceRenderState = {
			...currentState,
			placeholder
		};

		result = applyGridState(result, newGridState);
		return result;
	}
}

function applyTileDraggingInternal(
	currentState: SpaceRenderState,
	tileId: number,
	unboundGlobalPosition: GridPosition,
	shouldCommit: boolean
): SpaceRenderState {
	const {
		newState,
		tileBeingDragged,
		sourceId
	} = markTileAsBeingDragged(currentState, tileId);

	currentState = newState;

	const target: TileDropTarget = spaceSelectors.selectTileDropTarget(
		currentState,
		tileBeingDragged,
		unboundGlobalPosition
	);

	const context = new SpaceReducerContext();

	if (shouldCommit) {
		currentState = updateTileGroups(
			currentState,
			context,
			(group, groupContext) => tileGroupReducers.commitTileDragging(
				group,
				groupContext,
				tileBeingDragged,
				target,
				sourceId
			)
		);
	} else {
		currentState = updateTileGroups(
			currentState,
			context,
			(group, groupContext) => tileGroupReducers.applyTileDragging(
				group,
				groupContext,
				tileBeingDragged.data.id,
				target
			)
		);
	}

	return applyEffect(currentState, context);
}

function markTileAsBeingDragged(
	currentState: SpaceRenderState,
	tileId: number
): {
	newState: SpaceRenderState;
	tileBeingDragged: TileRenderState;
	sourceId: DatabaseOrTempObjectId;
} {
	let tileBeingDragged: TileRenderState | undefined;
	let sourceId: DatabaseOrTempObjectId | undefined;

	const newGroupsBuilder = new ModifiedCollectionBuilder<TileGroupRenderState>(currentState.groups);

	for (const groupRenderState of currentState.groups) {
		const newTilesBuilder = new ModifiedCollectionBuilder<TileRenderState>(
			groupRenderState.tileGrid
				.tiles
		);

		for (const tileRenderState of groupRenderState.tileGrid.tiles) {
			if (tileRenderState.data.id === tileId) {

				tileBeingDragged = tileReducers.setIsBeingDragged(
					tileRenderState,
					true
				);

				sourceId = groupRenderState.data
					.id;

				newTilesBuilder.push(tileBeingDragged);
			} else if (tileRenderState.isBeingDragged) {
				throw new Error("Some other tile is being dragged still");
			} else {
				newTilesBuilder.push(tileRenderState);
			}
		}

		const newTiles: readonly TileRenderState[] = newTilesBuilder.result();

		const newTileGrid: TileGridRenderState = setPropertiesPreservingRefWhenUnchanged(
			groupRenderState.tileGrid,
			{
				tiles: newTiles
			}
		);

		const newTileGroup: TileGroupRenderState = setPropertiesPreservingRefWhenUnchanged(
			groupRenderState,
			{
				tileGrid: newTileGrid
			}
		);

		newGroupsBuilder.push(newTileGroup);
	}

	if (isNil(tileBeingDragged) || isNil(sourceId)) {
		throw new Error(`Tile '${tileId}' does not exist in state: ${JSON.stringify(currentState)}`);
	}

	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			groups: newGroupsBuilder.result()
		}
	);

	return {
		newState: currentState,
		tileBeingDragged,
		sourceId
	};
}

function updateTileGroupAndApplyEffect(
	renderState: SpaceRenderState,
	id: DatabaseOrTempObjectId,
	callback: (tileGroupRenderState: TileGroupRenderState, context: TileGroupReducerContext) => TileGroupRenderState | "delete"
) {
	return updateTileGroupsAndApplyEffect(
		renderState,
		group => group.data
			.id
			.equals(id),
		callback
	);
}

function updateTileGroupsAndApplyEffect(
	renderState: SpaceRenderState,
	predicate: (tileGroupRenderState: TileGroupRenderState) => boolean,
	callback: (tileGroupRenderState: TileGroupRenderState, context: TileGroupReducerContext) => TileGroupRenderState | "delete"
): SpaceRenderState {
	const context = new SpaceReducerContext();

	renderState = updateTileGroups(
		renderState,
		context,
		callback,
		predicate
	);

	return applyEffect(renderState, context);
}

function updateTileGroup(
	currentState: SpaceRenderState,
	context: SpaceReducerContext,
	id: DatabaseOrTempObjectId,
	callback: (tileGroupRenderState: TileGroupRenderState, context: TileGroupReducerContext) => TileGroupRenderState | "delete",
): SpaceRenderState {
	return updateTileGroups(
		currentState,
		context,
		callback,
		g => g.data
			.id
			.equals(id)
	);
}

function updateTileGroups(
	currentState: SpaceRenderState,
	context: SpaceReducerContext,
	callback: (tileGroupRenderState: TileGroupRenderState, context: TileGroupReducerContext) => TileGroupRenderState | "delete",
	predicate?: (tileGroupRenderState: TileGroupRenderState) => boolean
): SpaceRenderState {
	const updatedGroups = new ModifiedCollectionBuilder<TileGroupRenderState>(currentState.groups);

	for (const groupCurrentState of currentState.groups) {
		if (!predicate || predicate(groupCurrentState)) {
			const groupResultState: TileGroupRenderState | "delete" = callback(
				groupCurrentState,
				context.createGroupContext()
			);

			if (groupResultState === "delete") {
				context.addEffect(SpaceChangeEffect.TileGroupGridRepackingIsRequired);
			} else {
				updatedGroups.push(groupResultState);
			}
		} else {
			updatedGroups.push(groupCurrentState);
		}
	}

	return setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			groups: updatedGroups.result()
		}
	);
}

function applyEffect(renderState: SpaceRenderState, context: SpaceReducerContext): SpaceRenderState {
	const effect: SpaceChangeEffect = context.resultEffect;

	if (hasFlag(effect, SpaceChangeEffect.TileGroupGridRepackingIsRequired)) {
		renderState = recalculateTileGroupPositions(renderState);
	}

	return renderState;
}

function recalculateTileGroupPositions(renderState: Omit<SpaceRenderState, "dimension">): SpaceRenderState {
	const tileGroupGridState: GridState<TileGroupRenderState> = spaceSelectors
		.generateStateOfTileGroupsConsumingSpace(renderState);

	return applyGridState(renderState, tileGroupGridState);
}

function applyGridState(
	currentState: Omit<SpaceRenderState, "dimension">,
	gridState: GridState<TileGroupRenderState | TileGroupPlaceHolderRenderState>
): SpaceRenderState {
	const {
		groups,
		placeholder
	} = currentState;

	const newPositionMap: Map<DatabaseOrTempObjectId | PlaceholderId, GridPosition> = fromArray(
		() => new DatabaseOrTempObjectIdMap<GridPosition>(),
		gridState.gridItems,
		(x) => x.item
			.data.id,
		(x) => x.position
	);

	const placeholderPosition: GridPosition | undefined = newPositionMap.get(PLACEHOLDER_ID);

	const newPlaceHolder: TileGroupPlaceHolderRenderState | undefined = placeholder && placeholderPosition
		? {
			...placeholder,
			position: placeholderPosition
		}
		: placeholder;

	const newGroups: readonly TileGroupRenderState[] = groups.map(
		(x): TileGroupRenderState => {
			const position: GridPosition | undefined = newPositionMap.get(x.data.id);
			if (!position) {
				return x;
			}

			return {
				...x,
				position
			};
		}
	);

	return {
		...currentState,
		groups: newGroups,
		placeholder: newPlaceHolder,
		dimension: getDimension(
			gridState,
			currentState.tileGroupGridDimensionLimits
		)
	};
}

function getPreferencesByTileGroupId(
	tileGroupPreferences: readonly TileGroupSpacePreferenceData[]
): ReadonlyMap<DatabaseOrTempObjectId, TileGroupInSpacePreference> {
	return fromArray(
		() => new DatabaseOrTempObjectIdMap<TileGroupInSpacePreference>(),
		tileGroupPreferences,
		(x) => x.tileGroupId,
		(x, i): TileGroupInSpacePreference => ({ isHidden: x.isHidden, order: i })
	);
}

function getOrderByTileGroupId(
	groups: readonly TileGroupRenderState[],
	preferencesMap: ReadonlyMap<DatabaseOrTempObjectId, TileGroupInSpacePreference>
): ReadonlyMap<DatabaseOrTempObjectId, number> {
	const groupRepresentationsForSorting: DatabaseOrTempObjectId[] = groups
		.map((x) => x.data.id);

	groupRepresentationsForSorting.sort((a, b) => {
		const aPreference: TileGroupInSpacePreference | undefined = preferencesMap.get(a);
		const bPreference: TileGroupInSpacePreference | undefined = preferencesMap.get(b);

		if (!aPreference && !bPreference) {
			return 0;
		}

		if (!aPreference) {
			return -1;
		}

		if (!bPreference) {
			return 1;
		}

		return aPreference.order - bPreference.order;
	});

	return fromArray(
		() => new DatabaseOrTempObjectIdMap(),
		groupRepresentationsForSorting,
		(x) => x,
		(_, i) => i
	);
}

function getDimension(
	gridState: GridState<TileGroupRenderState | TileGroupPlaceHolderRenderState>,
	dimensionLimits: DimensionLimitations
): GridDimension {
	return {
		rowSpan: clamp(
			gridState.dimension
				.rowSpan,
			dimensionLimits.minRows,
			dimensionLimits.maxRows
		),
		columnSpan: clamp(
			gridState.dimension
				.columnSpan,
			dimensionLimits.minColumns,
			dimensionLimits.maxColumns
		)
	};
}

function findTileGroupIdContainingTile(
	currentState: SpaceRenderState,
	tileId: number
): DatabaseOrTempObjectId {
	const tileGroup: TileGroupRenderState | undefined = currentState.groups
		.find(
			(x) => x.tileGrid
				.tiles
				.some((x) => x.data.id === tileId)
		);

	if (!tileGroup) {
		throw new Error(`Tile '${tileId}' does not belong to any tile group`);
	}

	return tileGroup.data
		.id;
}

type TileGroupInSpacePreference = {
	order: number;
	isHidden: boolean;
};
